/**
 * ScandiPWA - Progressive Web App for Magento
 *
 * Copyright © Scandiweb, Inc. All rights reserved.
 * See LICENSE for license details.
 *
 * @license OSL-3.0 (Open Software License ("OSL") v. 3.0)
 * @package scandipwa/base-theme
 * @link https://github.com/scandipwa/base-theme
 */

import PropTypes from 'prop-types';
import { PureComponent } from 'react';

import VfitsVehiclesByProductQuery from 'Query/VfitsVehiclesByProduct.query';

import VfitsVehiclesByProduct from './VfitsVehiclesByProduct.component';

/** @namespace Pwa/Component/VfitsVehiclesByProduct/Container/VfitsVehiclesByProductContainer */
export class VfitsVehiclesByProductContainer extends PureComponent {
    static propTypes = {
        product: PropTypes.shape({
            sku: PropTypes.string
        }).isRequired,
        onVehiclesLoad: PropTypes.func.isRequired
    };

    state = {
        vehicles: [],
        isLoading: false,
        isError: false
    };

    componentDidMount() {
        this.fetchVehicles();
    }

    componentDidUpdate(prevProps) {
        const { product: { sku } } = this.props;
        const { product: { sku: prevSku } } = prevProps;

        if (sku !== prevSku) {
            this.fetchVehicles();
        }
    }

    fetchVehicles = async () => {
        const { product: { sku } } = this.props;

        if (!sku) {
            return;
        }

        this.setState({
            isLoading: true,
            isError: false
        });

        try {
            // Use the query object to get fields, but build string manually for external GraphQL endpoint
            const fields = VfitsVehiclesByProductQuery._getVehicleFields();

            const queryString = `query GetVehiclesByProduct($sku: String!) {
                vehiclesByProduct(sku: $sku) {
                    ${fields.join('\n                    ')}
                }
            }`;

            const response = await fetch('https://fsync.frontrunneroutfitters.com/graphql/vfits', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    query: queryString,
                    variables: { sku }
                })
            });

            const result = await response.json();

            if (result && result.data && result.data.vehiclesByProduct) {
                const vehicles = result.data.vehiclesByProduct;
                this.setState({
                    vehicles,
                    isLoading: false,
                    isError: false
                });

                // Notify parent about vehicles availability
                const { onVehiclesLoad } = this.props;
                if (onVehiclesLoad) {
                    onVehiclesLoad(vehicles.length > 0);
                }
            } else {
                this.setState({
                    vehicles: [],
                    isLoading: false,
                    isError: false
                });

                // Notify parent about no vehicles
                const { onVehiclesLoad } = this.props;
                if (onVehiclesLoad) {
                    onVehiclesLoad(false);
                }
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error fetching vehicles:', error);
            this.setState({
                vehicles: [],
                isLoading: false,
                isError: true
            });

            // Notify parent about error (no vehicles)
            const { onVehiclesLoad } = this.props;
            if (onVehiclesLoad) {
                onVehiclesLoad(false);
            }
        }
    };

    containerProps() {
        const { vehicles, isLoading, isError } = this.state;

        return {
            vehicles,
            isLoading,
            isError
        };
    }

    render() {
        return (
            <VfitsVehiclesByProduct
              { ...this.containerProps() }
            />
        );
    }
}

export default VfitsVehiclesByProductContainer;
